import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Users, Calendar, UserPlus, Plus, MessageSquare } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import GroupChat from "@/components/training/GroupChat";
import UserAvatar from "@/components/shared/UserAvatar";

export default function CoachGroupDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("coach");
  const { user } = useAuth();
  const navigate = useNavigate();

  // ── Group data ──
  const { data: group, isLoading } = useQuery({
    queryKey: ["coach-group", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("training_groups")
        .select("id, name, level, max_players, color, coach_id, age_category")
        .eq("id", id!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // ── Members ──
  const { data: members = [] } = useQuery({
    queryKey: ["coach-group-members", id],
    queryFn: async () => {
      const { data } = await supabase
        .from("group_members")
        .select("id, player_id, status, profiles:player_id(full_name, level, avatar_url)")
        .eq("group_id", id!)
        .eq("status", "active");
      return (data ?? []) as any[];
    },
    enabled: !!id,
  });

  // ── Upcoming sessions ──
  const { data: sessions = [] } = useQuery({
    queryKey: ["coach-group-sessions", id],
    queryFn: async () => {
      const today = format(new Date(), "yyyy-MM-dd");
      const { data } = await supabase
        .from("training_sessions")
        .select("id, session_date, start_time, duration_minutes, status, courts(name), session_attendance(id, rsvp_status)")
        .eq("group_id", id!)
        .gte("session_date", today)
        .neq("status", "cancelled")
        .order("session_date")
        .order("start_time")
        .limit(10);
      return (data ?? []) as any[];
    },
    enabled: !!id,
  });

  if (isLoading) return <div className="space-y-4 pb-20 max-w-lg lg:max-w-4xl mx-auto"><Skeleton className="h-20" /><Skeleton className="h-60" /></div>;
  if (!group) return <p className="text-center py-12 text-muted-foreground">Grupp hittades inte</p>;

  return (
    <div className="pb-20 max-w-lg lg:max-w-6xl mx-auto">
      {/* ── Mobile header ── */}
      <div className="flex items-center gap-3 mb-4 lg:hidden">
        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate("/coach")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="h-4 w-4 rounded-full shrink-0" style={{ backgroundColor: group.color || "#6366f1" }} />
          <h1 className="text-lg font-semibold truncate">{group.name}</h1>
        </div>
        <div className="flex gap-1.5">
          {group.level && <Badge variant="outline" className="text-xs">{group.level}</Badge>}
          {group.age_category && <Badge variant="outline" className="text-xs">{group.age_category}</Badge>}
        </div>
      </div>

      {/* ── Desktop: sidebar + main ── */}
      <div className="lg:grid lg:grid-cols-[300px_1fr] lg:gap-8">

        {/* ── Desktop sidebar (hidden on mobile) ── */}
        <div className="hidden lg:block">
          <div className="sticky top-6 space-y-4">
            <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-2" onClick={() => navigate("/coach")}>
              <ArrowLeft className="h-3.5 w-3.5" /> {t("dashboard")}
            </Button>

            <Card className="border-l-4" style={{ borderLeftColor: group.color || "#6366f1" }}>
              <CardContent className="p-5 space-y-3">
                <h1 className="text-xl font-bold">{group.name}</h1>
                <div className="flex gap-1.5 flex-wrap">
                  {group.level && <Badge variant="outline" className="text-xs">{group.level}</Badge>}
                  {group.age_category && <Badge variant="outline" className="text-xs">{group.age_category}</Badge>}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{members.length} / {group.max_players ?? "∞"} {t("members")}</span>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-1.5">
              <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                <UserPlus className="h-3.5 w-3.5" /> {t("tabs.members")}
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => navigate("/manager/training")}>
                <Plus className="h-3.5 w-3.5" /> {t("createSession")}
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start gap-2">
                <MessageSquare className="h-3.5 w-3.5" /> {t("messageGroup")}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Main content with tabs ── */}
        <div>
          <Tabs defaultValue="members">
            <TabsList className="w-full overflow-x-auto flex-nowrap scrollbar-hide justify-start">
              <TabsTrigger value="members" className="shrink-0 text-xs">{t("tabs.members")}</TabsTrigger>
              <TabsTrigger value="sessions" className="shrink-0 text-xs">{t("tabs.sessions")}</TabsTrigger>
              <TabsTrigger value="chat" className="shrink-0 text-xs">{t("tabs.chat")}</TabsTrigger>
            </TabsList>

            {/* Members tab — responsive grid on desktop */}
            <TabsContent value="members" className="mt-3">
              <p className="text-xs text-muted-foreground mb-3 lg:hidden">{members.length} / {group.max_players ?? "∞"} {t("members")}</p>
              <div className="space-y-2 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
                {members.map((m: any) => {
                  const p = m.profiles;
                  return (
                    <Card key={m.id} className="lg:hover:shadow-sm lg:transition-shadow">
                      <CardContent className="p-3 flex items-center gap-3">
                        <UserAvatar avatarUrl={p?.avatar_url} fullName={p?.full_name} size="sm" className="lg:h-10 lg:w-10" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{p?.full_name ?? "?"}</p>
                        </div>
                        {p?.level && <Badge variant="outline" className="text-[10px]">Lv {p.level}</Badge>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            {/* Sessions tab */}
            <TabsContent value="sessions" className="mt-3 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t("noSessionsThisWeek")}</p>
              ) : (
                <div className="space-y-2 lg:grid lg:grid-cols-2 lg:gap-3 lg:space-y-0">
                  {sessions.map((s: any) => {
                    const attending = (s.session_attendance ?? []).filter((a: any) => a.rsvp_status === "attending").length;
                    return (
                      <Card key={s.id}>
                        <CardContent className="p-3 flex items-center gap-3">
                          <div className="text-center shrink-0 w-14">
                            <p className="text-sm font-bold">{s.start_time?.slice(0, 5)}</p>
                            <p className="text-[10px] text-muted-foreground">{format(new Date(s.session_date), "d MMM", { locale: sv })}</p>
                          </div>
                          <div className="flex-1 min-w-0 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {(s.courts as any)?.name ?? "—"}</span>
                            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {attending} {t("enrolled")}</span>
                          </div>
                          <Badge variant={s.status === "scheduled" ? "secondary" : "outline"} className="text-[10px]">{s.status}</Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Chat tab — full height on desktop */}
            <TabsContent value="chat" className="mt-3 lg:min-h-[500px]">
              <GroupChat
                groupId={id!}
                groupName={group.name}
                memberCount={members.length}
                isCoachOrAdmin={true}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
